/**
 * Ranch → Access and sharing. Who can do what by role, links shared with a vet or partner,
 * connected devices, the activity trail and who's signed in. Changing roles stays in Settings.
 */
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { ROLE_DESCRIPTIONS, ROLE_LABELS, type Permission, type Role } from "../../../contract";
import { api, qs } from "../../../web/lib/api";
import { formatDate, timeAgo } from "../../../web/lib/format";
import { plural, t, useLanguage } from "../../../web/lib/i18n";
import { invalidateAll, useApi } from "../../../web/lib/query";
import { useCan } from "../../../web/lib/session";
import { RefPicker, useCrew } from "../../../web/forms/pickers";
import { Button, Card, Checkbox, Chip, Empty, ErrorNote, Field, Icon, List, Loading, PageHeader, Row, Section, Segmented, Select, Sheet, Tabs, TextInput, toast } from "../../../web/ui/kit";
import { Page } from "../../../web/shell/Layout";
import type { AuditEntryDTO, DeviceTokenDTO, SessionDTO, ShareCategory, ShareDTO } from "../shared/types";
import { CATEGORY_LABEL } from "./labels";

const PERMISSION_WORDS: Record<Permission, string> = {
  read: "See cattle, places, work and history", record: "Record work and notes", review: "File anyone's notes", manage: "Change records, jobs and settings",
  "finance.read": "See finances", "finance.write": "Change finances", people: "Invite crew, share records, see activity", data: "Bring in files and download everything",
};

export function AccessPage() {
  useLanguage();
  const can = useCan();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") ?? "roles";
  if (!can("manage")) {
    return <Page><PageHeader back={{ to: "/ranch", label: t("Ranch") }} title={t("Access and sharing")} /><Empty icon="shield" title={t("Only owners and managers can see this.")} /></Page>;
  }
  const tabs = [
    { id: "roles", label: t("Who sees what") },
    ...(can("people") ? [{ id: "shares", label: t("Shared links") }] : []),
    { id: "devices", label: t("Devices") },
    ...(can("people") ? [{ id: "activity", label: t("Activity") }, { id: "sessions", label: t("Signed in") }] : []),
  ];
  return (
    <Page>
      <PageHeader back={{ to: "/ranch", label: t("Ranch") }} title={t("Access and sharing")} subtitle={t("Who sees what, links you've shared, devices and recent activity.")} />
      <Tabs value={tab} onChange={(id) => setParams(id === "roles" ? {} : { tab: id }, { replace: true })} tabs={tabs} />
      {tab === "roles" && <Roles />}
      {tab === "shares" && can("people") && <Shares />}
      {tab === "devices" && <Devices />}
      {tab === "activity" && can("people") && <Activity />}
      {tab === "sessions" && can("people") && <Sessions />}
    </Page>
  );
}

function Roles() {
  const q = useApi<{ items: Array<{ role: Role; people: number; permissions: Record<Permission, boolean> }> }>("/m/data/access/roles");
  if (q.isLoading) return <Loading />;
  if (!q.data) return <ErrorNote error={q.error} />;
  return (
    <div className="stack">
      <p className="muted">{t("Every rule is checked on the server, in search, Ask, downloads and shared links, not just on buttons.")} <Link to="/settings">{t("Change someone's role in Settings.")}</Link></p>
      <div className="data-roles">
        {q.data.items.map((r) => (
          <Card key={r.role}>
            <h3>{t(ROLE_LABELS[r.role])} <Chip tone="neutral">{plural(r.people, "{{count}} person", "{{count}} people")}</Chip></h3>
            <p className="muted">{t(ROLE_DESCRIPTIONS[r.role])}</p>
            <ul>{(Object.keys(PERMISSION_WORDS) as Permission[]).map((p) => (
              <li key={p} className={r.permissions[p] ? undefined : "is-no"}><Icon name={r.permissions[p] ? "check" : "minus"} size={16} />{t(PERMISSION_WORDS[p])}</li>
            ))}</ul>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- shared links

const CATEGORIES: ShareCategory[] = ["identity", "health", "breeding", "weights", "movements", "herd", "genetics", "dairy", "notes"];

function Shares() {
  const q = useApi<{ items: ShareDTO[] }>("/m/data/shares");
  const [open, setOpen] = useState(false);
  const revoke = async (s: ShareDTO) => {
    await api.post(`/m/data/shares/${s.id}/revoke`);
    toast(t("Link turned off. Anyone who opens it now sees that it's off."), { tone: "good" });
    await q.refetch();
  };
  return (
    <div className="stack">
      <Card>
        <p>{t("Send a vet, buyer or partner a read-only link to the records they need. You choose the animals and what they see; prices and finances are never included.")}</p>
        <div className="row-actions"><Button variant="primary" size="lg" icon="share" onClick={() => setOpen(true)}>{t("Share records")}</Button></div>
      </Card>
      {q.isLoading ? <Loading /> : !q.data?.items.length ? <p className="muted">{t("No links shared yet.")}</p> : (
        <List>{q.data.items.map((s) => (
          <Row key={s.id} icon="share" title={s.recipient}
            meta={[plural(s.animals, "{{count}} animal", "{{count}} animals"), s.categories.map((c) => t(CATEGORY_LABEL[c])).join(", "),
              s.state === "active" ? t("until {{date}}", { date: formatDate(s.expires_at.slice(0, 10)) }) : null, plural(s.views, "opened {{count}} time", "opened {{count}} times"),
              s.last_viewed_at ? t("last {{when}}", { when: timeAgo(s.last_viewed_at) }) : null].filter(Boolean).join(" · ")}
            trailing={s.state === "active" ? <Button size="sm" variant="ghost" onClick={() => void revoke(s)}>{t("Turn off")}</Button> : <Chip tone="neutral">{t(s.state === "expired" ? "Expired" : "Turned off")}</Chip>} />
        ))}</List>
      )}
      {open && <ShareSheet onClose={() => { setOpen(false); void q.refetch(); }} />}
    </div>
  );
}

function ShareSheet({ onClose }: { onClose: () => void }) {
  const [recipient, setRecipient] = useState("");
  const [contact, setContact] = useState<string | null>(null);
  const [purpose, setPurpose] = useState("");
  const [group, setGroup] = useState<string | null>(null);
  const [animals, setAnimals] = useState<string[]>([]);
  const [cats, setCats] = useState<ShareCategory[]>(["identity", "health"]);
  const [days, setDays] = useState("14");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [made, setMade] = useState<{ url: string; share: ShareDTO } | null>(null);
  const toggle = (c: ShareCategory, v: boolean) => setCats((list) => (v ? [...list, c] : list.filter((x) => x !== c)));
  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      setMade(await api.post<{ url: string; share: ShareDTO }>("/m/data/shares", { recipient, contact_id: contact, purpose: purpose || null, group_id: group, animal_ids: animals, categories: cats, days: Number(days) }));
      await invalidateAll();
    } catch (err) { setError(err); } finally { setBusy(false); }
  };
  if (made) {
    return (
      <Sheet open onClose={onClose} title={t("Link ready")} footer={<Button variant="primary" size="lg" block onClick={onClose}>{t("Done")}</Button>}>
        <p>{t("Send this to {{recipient}}. It shows {{count}} animals and works until {{date}}.", { recipient: made.share.recipient, count: made.share.animals, date: formatDate(made.share.expires_at.slice(0, 10)) })}</p>
        <div className="data-secret"><code>{made.url}</code><Button icon="share" onClick={() => void navigator.clipboard?.writeText(made.url).then(() => toast(t("Copied")))}>{t("Copy")}</Button></div>
        <p><a href={made.url} target="_blank" rel="noopener noreferrer">{t("See what they'll see")}</a></p>
        <p className="muted">{t("Anyone with the link can see these records until it expires or you turn it off. Turning it off can't take back what was already downloaded.")}</p>
      </Sheet>
    );
  }
  return (
    <Sheet open onClose={onClose} title={t("Share records")} footer={<Button variant="primary" size="lg" block busy={busy} disabled={!recipient.trim() || !cats.length || (!group && !animals.length)} onClick={() => void create()}>{t("Make the link")}</Button>}>
      <Field label={t("Who is it for")}><TextInput value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder={t("Dr. Rivera")} /></Field>
      <Field label={t("Their contact")} optional><RefPicker target="contact" value={contact} onChange={(v) => { setContact(v as string | null); }} /></Field>
      <Field label={t("Why")} optional><TextInput value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder={t("Pre-breeding exams")} /></Field>
      <Field label={t("Which cattle")} hint={t("The animals are fixed when you make the link. Later changes to the group don't change what's shared.")}>
        <RefPicker target="group" value={group} onChange={(v) => setGroup(v as string | null)} placeholder={t("A group")} />
      </Field>
      <Field label={t("Or pick animals")} optional><RefPicker target="animal" multiple value={animals} onChange={(v) => setAnimals((v as string[]) ?? [])} /></Field>
      <Field label={t("What they can see")} hint={t("Prices and finances are never shared.")}>
        <div className="data-choices">{CATEGORIES.map((c) => (
          <label key={c} className={`choice${cats.includes(c) ? " choice-on" : ""}`}><Checkbox checked={cats.includes(c)} onChange={(v) => toggle(c, v)} label={t(CATEGORY_LABEL[c])} /> {t(CATEGORY_LABEL[c])}</label>
        ))}</div>
      </Field>
      <Field label={t("For how long")}>
        <Segmented label={t("For how long")} value={days} onChange={setDays} options={[{ value: "3", label: t("3 days") }, { value: "14", label: t("2 weeks") }, { value: "30", label: t("30 days") }, { value: "90", label: t("90 days") }]} />
      </Field>
      {error ? <ErrorNote error={error} /> : null}
    </Sheet>
  );
}

// ---------------------------------------------------------------- devices

function Devices() {
  const q = useApi<{ items: DeviceTokenDTO[]; url: string }>("/m/data/devices");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [secret, setSecret] = useState<{ token: string; url: string; label: string } | null>(null);
  const [adding, setAdding] = useState(false);
  const create = async () => {
    setBusy(true);
    try { const r = await api.post<{ token: string; url: string; device: DeviceTokenDTO }>("/m/data/devices", { label }); setSecret({ token: r.token, url: r.url, label: r.device.label }); setAdding(false); setLabel(""); await q.refetch(); } finally { setBusy(false); }
  };
  const rotate = async (d: DeviceTokenDTO) => { const r = await api.post<{ token: string; url: string; device: DeviceTokenDTO }>(`/m/data/devices/${d.id}/rotate`); setSecret({ token: r.token, url: r.url, label: r.device.label }); await q.refetch(); };
  const revoke = async (d: DeviceTokenDTO) => { await api.post(`/m/data/devices/${d.id}/revoke`); toast(t("Key turned off.")); await q.refetch(); };
  return (
    <div className="stack">
      <Card>
        <p>{t("A push-to-talk button, recorder or shortcut can send voice notes straight into Hondo. They're reviewed like any note, as the person who made the key.")}</p>
        <div className="row-actions"><Button variant="primary" size="lg" icon="radio" onClick={() => setAdding(true)}>{t("Connect a device")}</Button></div>
      </Card>
      {q.isLoading ? <Loading /> : !q.data?.items.length ? <p className="muted">{t("No devices connected.")}</p> : (
        <List>{q.data.items.map((d) => (
          <Row key={d.id} icon="radio" title={d.label}
            meta={[d.owner?.label, t("key ends {{hint}}", { hint: d.token_hint }), d.last_used_at ? t("last used {{when}}", { when: timeAgo(d.last_used_at) }) : t("never used"), d.revoked_at ? t("off") : null].filter(Boolean).join(" · ")}
            trailing={d.revoked_at ? <Chip tone="neutral">{t("Off")}</Chip> : <span className="row-actions"><Button size="sm" variant="ghost" onClick={() => void rotate(d)}>{t("New key")}</Button><Button size="sm" variant="ghost" onClick={() => void revoke(d)}>{t("Turn off")}</Button></span>} />
        ))}</List>
      )}
      <Sheet open={adding} onClose={() => setAdding(false)} title={t("Connect a device")} footer={<Button variant="primary" size="lg" block busy={busy} disabled={!label.trim()} onClick={() => void create()}>{t("Make a key")}</Button>}>
        <Field label={t("Name it")}><TextInput value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t("Barn push-to-talk")} /></Field>
      </Sheet>
      <Sheet open={!!secret} onClose={() => setSecret(null)} title={secret?.label ?? ""} footer={<Button variant="primary" size="lg" block onClick={() => setSecret(null)}>{t("I copied it")}</Button>}>
        <p>{t("Put these in the device's webhook settings. The key is shown only once.")}</p>
        <Field label={t("Webhook URL")}><div className="data-secret"><code>{secret?.url}</code><Button onClick={() => void navigator.clipboard?.writeText(secret?.url ?? "").then(() => toast(t("Copied")))}>{t("Copy")}</Button></div></Field>
        <Field label={t("Authorization")}><div className="data-secret"><code>{`Bearer ${secret?.token ?? ""}`}</code><Button onClick={() => void navigator.clipboard?.writeText(`Bearer ${secret?.token ?? ""}`).then(() => toast(t("Copied")))}>{t("Copy")}</Button></div></Field>
        <p className="muted">{t("Send POST multipart/form-data with an audio file or a transcript, and an Idempotency-Key header so retries never make two notes.")}</p>
      </Sheet>
    </div>
  );
}

// ---------------------------------------------------------------- activity

const ACTION_WORDS: Record<string, string> = {
  create: "Added", update: "Changed", correct: "Corrected", void: "Removed", merge: "Merged",
  "import.upload": "Uploaded files to bring in", "import.commit": "Started filing an import", "import.committed": "Filed an import", "import.undo": "Undid an import",
  "import.settle": "Settled an import question", "import.discard": "Set an import aside", "import.download": "Downloaded an original file", "import.send_to_hondo": "Sent a file to Hondo",
  "import.restore": "Started restoring a backup", "import.restored": "Restored a backup",
  "export.full": "Downloaded everything", "export.download": "Downloaded a saved export", "export.csv": "Downloaded a list", "export.evidence": "Downloaded an evidence packet",
  "share.create": "Shared records", "share.revoke": "Turned off a shared link", "share.view": "Opened a shared link", "share.download": "Downloaded from a shared link",
  "device.create": "Connected a device", "device.rotate": "Made a new device key", "device.revoke": "Turned off a device key", "device.capture": "A device sent a note",
  "session.revoke": "Signed someone out", "session.revoke_all": "Signed everyone else out",
  "exchange.prepare": "Prepared a partner file", "exchange.sent": "Marked a partner file sent", "exchange.answer": "Recorded a partner's answer", "exchange.download": "Downloaded a partner file",
  "program.decision": "Recorded a program decision",
};

const ACTION_FILTERS: Array<{ value: string; label: string }> = [
  { value: "", label: "Everything" }, { value: "share", label: "Shared links" }, { value: "export", label: "Downloads" }, { value: "import", label: "Imports" },
  { value: "device", label: "Devices" }, { value: "session", label: "Sign-ins" }, { value: "void", label: "Removed records" }, { value: "correct", label: "Corrections" },
];

function Activity() {
  const crew = useCrew();
  const [actor, setActor] = useState("");
  const [action, setAction] = useState("");
  const [from, setFrom] = useState("");
  const [pages, setPages] = useState<string[]>([""]);
  return (
    <div className="stack">
      <div className="filter-row">
        <Field label={t("Who")}><Select value={actor} onChange={(e) => { setActor(e.target.value); setPages([""]); }}>
          <option value="">{t("Anyone")}</option>
          <option value="link">{t("Someone with a shared link")}</option>
          {(crew.data?.items ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select></Field>
        <Field label={t("What")}><Select value={action} onChange={(e) => { setAction(e.target.value); setPages([""]); }}>{ACTION_FILTERS.map((a) => <option key={a.value} value={a.value}>{t(a.label)}</option>)}</Select></Field>
        <Field label={t("Since")} optional><TextInput type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPages([""]); }} /></Field>
      </div>
      {pages.map((before, i) => <ActivityPage key={`${actor}|${action}|${from}|${before}`} q={{ actor, action, from, before }} last={i === pages.length - 1} more={(next) => setPages((p) => [...p, next])} />)}
    </div>
  );
}

function ActivityPage({ q, last, more }: { q: Record<string, string>; last: boolean; more: (next: string) => void }) {
  const r = useApi<{ items: AuditEntryDTO[]; next: string | null }>(`/m/data/access/audit${qs(q)}`);
  if (r.isLoading) return <Loading />;
  if (!r.data) return <ErrorNote error={r.error} />;
  if (!r.data.items.length && !q.before) return <p className="muted">{t("Nothing recorded for these filters.")}</p>;
  return (
    <>
      <List>{r.data.items.map((e) => (
        <Row key={e.id} icon={e.action.startsWith("share") ? "share" : e.action.startsWith("export") ? "download" : e.action.startsWith("import") ? "upload" : e.action.startsWith("device") ? "radio" : "history"}
          title={`${e.actor?.label ?? (e.action.startsWith("share.") ? t("Someone with a shared link") : t("Hondo"))}: ${t(ACTION_WORDS[e.action] ?? e.action)}`}
          meta={[e.entity_label, typeof e.detail?.recipient === "string" ? t("for {{name}}", { name: e.detail.recipient }) : null, formatDate(e.at, "minute")].filter(Boolean).join(" · ")} />
      ))}</List>
      {last && r.data.next && <Button variant="ghost" block onClick={() => more(r.data!.next!)}>{t("Show older")}</Button>}
    </>
  );
}

function Sessions() {
  const q = useApi<{ items: SessionDTO[] }>("/m/data/access/sessions");
  const [busy, setBusy] = useState(false);
  const end = async (s: SessionDTO) => { await api.post(`/m/data/access/sessions/${s.id}/revoke`); toast(t("Signed out.")); await q.refetch(); };
  const endOthers = async () => {
    setBusy(true);
    try { const r = await api.post<{ revoked: number }>("/m/data/access/sessions/revoke-others"); toast(plural(r.revoked, "Signed out {{count}} device.", "Signed out {{count}} devices.")); await q.refetch(); } finally { setBusy(false); }
  };
  if (q.isLoading) return <Loading />;
  return (
    <div className="stack">
      <p className="muted">{t("Signing someone out ends that sign-in right away. To remove someone from the ranch, change them in Settings.")}</p>
      <List>{(q.data?.items ?? []).map((s) => (
        <Row key={s.id} icon="people" title={s.person} meta={[s.role ? t(ROLE_LABELS[s.role as Role] ?? s.role) : null, s.kind === "web" ? t("Browser or phone") : t("Device"), s.last_used_at ? t("active {{when}}", { when: timeAgo(s.last_used_at) }) : null, s.current ? t("this device") : null].filter(Boolean).join(" · ")}
          trailing={s.current ? <Chip tone="info">{t("You")}</Chip> : <Button size="sm" variant="ghost" onClick={() => void end(s)}>{t("Sign out")}</Button>} />
      ))}</List>
      <div className="row-actions"><Button variant="danger" busy={busy} onClick={() => void endOthers()}>{t("Sign out everyone else")}</Button></div>
    </div>
  );
}
